import { useRef } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useCreateOrderMutation } from "../../../../../../../../API/authApi";
import { useToastify } from "../../../../../../../../Hooks/hooks";
import { IDineInOrderForm } from "../../../../../../../../Interfaces/formValue-interfaces";
import { INewOrder } from "../../../../../../../../Interfaces/request-interfaces";
import { IDineInOrderData, IOrderParams, OrderTypes } from "../../../../../../../../Interfaces/componentTypes";
import useDialogHooks from "../../../../../../../../Components/Dialog/dialog-hooks";
import { IDineInOrderProps } from "./dineInOrderDialog";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import OrderSuccess from "../OrderSuccess/orderSuccess";
import SmsCodeDialog from "../SMSCode/smsCodeDialog";
import { useOrderPayment } from "../../OrderPayment/useOrderPayment";
import { getOrderTimeValidationMessageKey, getNextOrderDate, isBusinessClosed, isBusinessFreeDay, validateOrderDateTime } from "../../../../../../../../Utilits/utilits";
import useOrderScheduleValidation from "../../OrderScheduleNotices/useOrderScheduleValidation";

const useDineInOrderHook = (props: IDineInOrderProps) => {
    const {
        companyId,
        company_name,
        company_logo,
        company_contacts,
        cart,
        dineInPrice,
        serviceFee,
        pos_terminal,
        setDialog,
        onCloseDialog,
        setMainCart,
        totalItemCount,
        setContentType,
        dineInLocation,
        business_profile,
    } = props;
    const [sendOrder, { isLoading: newOrderLoading }] = useCreateOrderMutation();
    const user_id = localStorage.getItem('user_id');
    const phone = localStorage.getItem('phone');
    const { open, onOpen: onOpenNoteDialog, onClose: onCloseNoteDialog } = useDialogHooks();
    const dialogRef = useRef<HTMLDivElement>(null);
    const { t } = useLanguagesHooks();
    const { showToastError } = useToastify();
    const normalizedTotalPrice = Number(dineInPrice) || 0;
    const isScheduleRequired = isBusinessClosed(business_profile);

    const {
        register,
        control,
        formState: { errors },
        getValues,
        setValue,
        setError,
        clearErrors,
        watch,
    } = useForm<IDineInOrderForm>({
        mode: 'onChange',
        defaultValues: {
            phone_number: phone ?? '',
            date: business_profile && isScheduleRequired
                ? getNextOrderDate(business_profile)
                : moment().format('YYYY-MM-DD'),
            time: undefined,
            notes: '',
            cash: true,
            card: false,
        },
    });

    useOrderScheduleValidation({
        business_profile,
        watch,
        setValue,
        setError,
        clearErrors,
        enabled: isScheduleRequired,
    });

    const { getPaymentParams } = useOrderPayment(pos_terminal, setValue, watch, getValues);

    const handleNote = (note: string) => {
        setValue('notes', note.trim() || undefined);
        onCloseNoteDialog();
    };

    const onOrder = async () => {
        if (!dineInLocation) {
            return;
        }

        const phoneNumber = getValues('phone_number').trim().replace(/\s+/g, '');
        if (phoneNumber.length < 12) {
            setError('phone_number', { message: 'dxgcghfhg' });
            dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const orderDate = getValues('date');
        const orderTime = getValues('time');

        if (isScheduleRequired) {
            if (!orderTime) {
                setError('time', { type: 'required', message: '' });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            if (business_profile && orderDate && isBusinessFreeDay(orderDate, business_profile.free_days)) {
                setError('date', { message: t(getOrderTimeValidationMessageKey('closed_day')) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            const timeValidation = validateOrderDateTime(orderDate, orderTime, business_profile);
            if (!timeValidation.valid) {
                setError('time', { message: t(getOrderTimeValidationMessageKey(timeValidation.reason)) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        } else if (orderTime) {
            if (business_profile && orderDate && isBusinessFreeDay(orderDate, business_profile.free_days)) {
                setError('date', { message: t(getOrderTimeValidationMessageKey('closed_day')) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            const timeValidation = validateOrderDateTime(orderDate, orderTime, business_profile);
            if (!timeValidation.valid) {
                setError('time', { message: t(getOrderTimeValidationMessageKey(timeValidation.reason)) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        const data: IDineInOrderData = {
            cart,
            service_fee: serviceFee,
            total_cost: normalizedTotalPrice,
            location_type: dineInLocation.location_type,
            location_number: dineInLocation.location_number,
        };

        const params: IOrderParams = {
            phone_number: phoneNumber,
            notes: getValues('notes'),
            date: getValues('date'),
            time: getValues('time'),
            ...getPaymentParams(),
            totalItems: totalItemCount,
            company_id: companyId,
            company_name,
            company_logo,
            company_contacts,
            location_type: dineInLocation.location_type,
            location_number: dineInLocation.location_number,
        };

        const payload: INewOrder = {
            company_id: companyId,
            order_type: OrderTypes.DINE_IN,
            phone_number: phoneNumber,
            uuid: user_id,
            data: JSON.stringify(data),
            order_parameters: JSON.stringify(params),
            location_type: dineInLocation.location_type,
            location_number: dineInLocation.location_number,
        };

        try {
            await sendOrder(payload).unwrap();
            setMainCart([]);
            setDialog(
                <OrderSuccess
                    setContentType={setContentType}
                    onClose={onCloseDialog}
                />
            );
        } catch (error: any) {
            const status = error?.error;
            if (status === 419) {
                setDialog(
                    <SmsCodeDialog
                        data={payload}
                        setDialog={setDialog}
                        setMainCart={setMainCart}
                        onCloseDialog={onCloseDialog}
                        setContentType={setContentType}
                    />
                );
            } else {
                showToastError(t('Toast.Error.Common'));
            }
        }
    };

    return {
        open,
        errors,
        control,
        dialogRef,
        newOrderLoading,
        isScheduleRequired,
        onOrder,
        register,
        getValues,
        watch,
        handleNote,
        onOpenNoteDialog,
        onCloseNoteDialog,
        normalizedTotalPrice,
    };
};

export default useDineInOrderHook;
